/**
 * The authorization request a credential screen is standing in the middle of.
 *
 * Every screen in the sign-in path needs the same three things: the handle to
 * carry forward, the application's name for the copy, and a way to hand control
 * back to `/authorize` when the user is finally ready. This puts them in one
 * place so the screens stay about their own job.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { AuthRequestSummary } from '@mindpeer/auth-shared';
import {
  authRequestFrom,
  fetchAuthRequest,
  resumeAuthorization,
  withAuthRequest,
} from '@/lib/auth-request';

export interface AuthRequestState {
  /** Opaque handle, or null on a direct visit to the portal. */
  handle: string | null;
  /** True when the user arrived from a module rather than directly. */
  isModuleFlow: boolean;
  /** The module's registration, once loaded. Null while loading or unknown. */
  request: AuthRequestSummary | null;
  /** Module display name, or null when there is nothing to show. */
  applicationName: string | null;
  /** Hand control back to `/authorize`. Returns false when there is no flow. */
  resume: () => boolean;
  /** Add the handle to an internal link so the flow survives navigation. */
  link: (path: string) => string;
}

export function useAuthRequest(): AuthRequestState {
  const location = useLocation();
  const handle = authRequestFrom(location.search);
  const [request, setRequest] = useState<AuthRequestSummary | null>(null);

  useEffect(() => {
    if (!handle) {
      setRequest(null);
      return;
    }
    let cancelled = false;
    void fetchAuthRequest(handle).then((summary) => {
      if (!cancelled) setRequest(summary);
    });
    return () => {
      cancelled = true;
    };
  }, [handle]);

  const resume = useCallback((): boolean => {
    if (!handle) return false;
    resumeAuthorization(handle);
    return true;
  }, [handle]);

  const link = useCallback((path: string) => withAuthRequest(path, handle), [handle]);

  return {
    handle,
    isModuleFlow: handle !== null,
    request,
    applicationName: request?.application.name ?? null,
    resume,
    link,
  };
}
